import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Pencil, Trash2, Tag, Loader2, Check, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Manage the investor's own list of vendor categories (Plumbing, HVAC, …).
 * - Add a new category (POST /api/vendor-categories)
 * - Rename (PUT /api/vendor-categories/{id}) — cascades to all vendors using the old label
 * - Delete (DELETE /api/vendor-categories/{id}) — 409 if in use; force=true clears vendor.category
 *
 * Props:
 *   open         — boolean
 *   onClose      — () => void
 *   onChanged    — called after any successful mutation so the parent can refresh its list
 */
export default function VendorCategoriesDialog({ open, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor-categories");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/vendor-categories", { name });
      setNewName("");
      toast.success(`Added "${name}"`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add category");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditValue(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditValue(""); };

  const saveEdit = async (c) => {
    const name = editValue.trim();
    if (!name || name === c.name) { cancelEdit(); return; }
    try {
      const { data } = await api.put(`/vendor-categories/${c.id}`, { name });
      cancelEdit();
      toast.success(
        data.updated_vendors > 0
          ? `Renamed. ${data.updated_vendors} vendor${data.updated_vendors === 1 ? "" : "s"} updated.`
          : "Renamed."
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to rename");
    }
  };

  const remove = async (c) => {
    if (c.vendor_count > 0) {
      const ok = window.confirm(
        `"${c.name}" is used by ${c.vendor_count} vendor${c.vendor_count === 1 ? "" : "s"}.\n\n` +
        `Delete anyway? Those vendors will have their category cleared (you can re-assign later).`
      );
      if (!ok) return;
      try {
        await api.delete(`/vendor-categories/${c.id}?force=true`);
        toast.success(`Deleted "${c.name}" and cleared ${c.vendor_count} vendor${c.vendor_count === 1 ? "" : "s"}`);
        await load();
        onChanged?.();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
      }
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/vendor-categories/${c.id}`);
      toast.success(`Deleted "${c.name}"`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="vendor-categories-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Tag className="w-5 h-5" /> Manage vendor categories
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={create} className="flex gap-2 pb-3 border-b border-slate-100">
          <input
            type="text"
            className="input-clean flex-1"
            placeholder="New category name (e.g., Appliance Repair)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={60}
            data-testid="vendor-category-new-input"
          />
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            className="btn-solid-dark text-sm flex items-center gap-1"
            data-testid="vendor-category-add-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </form>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-sm text-slate-400 flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="vendor-categories-empty">
              No categories yet — add your first one above.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((c) => (
                <li key={c.id} className="py-2 flex items-center gap-2" data-testid={`vendor-category-row-${c.id}`}>
                  {editingId === c.id ? (
                    <>
                      <input
                        type="text"
                        autoFocus
                        className="input-clean flex-1 text-sm"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        maxLength={60}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(c);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        data-testid={`vendor-category-edit-input-${c.id}`}
                      />
                      <button
                        onClick={() => saveEdit(c)}
                        className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-sm"
                        aria-label="Save"
                        data-testid={`vendor-category-save-${c.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-slate-500 hover:bg-slate-100 p-1.5 rounded-sm"
                        aria-label="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{c.name}</span>
                          {c.is_default && (
                            <span className="text-[9px] font-mono uppercase tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">default</span>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">
                            {c.vendor_count === 0
                              ? "Unused"
                              : `${c.vendor_count} vendor${c.vendor_count === 1 ? "" : "s"}`}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => startEdit(c)}
                        className="text-slate-500 hover:text-slate-900 p-1.5 rounded-sm hover:bg-slate-100"
                        aria-label="Rename"
                        data-testid={`vendor-category-edit-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        className="text-red-500 hover:text-red-700 p-1.5 rounded-sm hover:bg-red-50"
                        aria-label="Delete"
                        data-testid={`vendor-category-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark text-sm" data-testid="vendor-categories-close">
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
